/**
 * React Query hook for the V3 unified search — POST /v3/search/listings.
 * Listings are fetched based on map bounds and search filters.
 */

import { useQuery } from '@zg-rentals/react-query5';
import {
  fetchSearchUnitCount,
  searchListings,
  type SearchListingsRequest,
  type SearchListingsResult,
} from '@/src/lib/api/endpoints/search';
import { capV3Photos } from '@/src/lib/utils/v3Listings';
import { searchKeys } from './queryKeys';

const SEARCH_STALE_TIME = 5 * 60 * 1000;

const SEARCH_QUERY_META = {
  tags: ['search-listings'],
  context: {
    api: 'hotpads-api',
    details: 'V3 search listings',
  },
};

// Its own tag and details: probes are best-effort (`retry: false`), and up to 9
// fire per zero-result page. Sharing the primary search's meta reported their
// failures as SRP search failures and let a `search-listings` invalidation evict
// them, re-triggering the whole fan-out.
const SEARCH_UNIT_COUNT_META = {
  tags: ['search-unit-count'],
  context: {
    api: 'hotpads-api',
    details: 'V3 search unit count',
  },
};

function hasFiniteBoundingBox(request: SearchListingsRequest): boolean {
  const box = request.filters?.boundingBox;
  if (!box) return false;
  return [box.minLat, box.maxLat, box.minLon, box.maxLon].every(
    (value) => typeof value === 'number' && Number.isFinite(value),
  );
}

/**
 * Query options for a count-only V3 search: same endpoint, caching, and
 * observability envelope as {@link useSearchListings}, but the cache entry holds
 * just the matching-unit total.
 *
 * Exists so callers that need a bare count (the SRP zero-results probes, which
 * issue one per active filter) share this hook's policy instead of restating it
 * — and so `page.limit` stays at 1, since the listing bodies are discarded.
 */
export function searchUnitCountQueryOptions(request: SearchListingsRequest) {
  return {
    queryKey: searchKeys.unitCount(request),
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchSearchUnitCount(request, signal),
    enabled: hasFiniteBoundingBox(request),
    staleTime: SEARCH_STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
    meta: SEARCH_UNIT_COUNT_META,
  };
}

/**
 * Fetch listings from the V3 unified search endpoint (SRP grid + HDP nearby
 * pins): keyed on the `SearchListingsRequest`, refetches on
 * viewport/filter/page changes, seeds from SSR via `initialData`, and keeps
 * previous data during refetch. Photos are capped before the cache stores them.
 */
export const useSearchListings = (
  request: SearchListingsRequest,
  options?: {
    enabled?: boolean;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    initialData?: SearchListingsResult;
  },
) => {
  return useQuery<SearchListingsResult, Error>({
    queryKey: searchKeys.listings(request),
    queryFn: async ({ signal }) => {
      const response = await searchListings(request, signal);
      return { ...response, listings: capV3Photos(response.listings) };
    },
    enabled: options?.enabled !== false && hasFiniteBoundingBox(request),
    staleTime: options?.staleTime ?? SEARCH_STALE_TIME,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnMount: options?.refetchOnMount ?? false,
    initialData: options?.initialData,
    placeholderData: (previousData) => previousData,
    meta: SEARCH_QUERY_META,
  });
};
